import React from 'react';
import { Truck as TruckIcon } from 'lucide-react';

/**
 * B3 Fix Plate button.
 *
 * One component for every surface that offers the fix — the Port node card
 * inside the task detail modal, and the task rows on both dispatch boards.
 * The button IS the alert: red and pulsing means the eModal appointment still
 * carries the placeholder/stale plate, so there is no separate marker to keep
 * in sync with it.
 *
 * 'verified' is only rendered inside the task detail modal. A list showing a
 * green tick on every correct row would be noise — the lists render nothing
 * at all when there is nothing to fix.
 */
/**
 * 'unlinked' is for an appointment that exists on eModal but was recorded
 * here by hand, so we don't yet know which eModal row it is. The action is
 * still offered — the first click walks the operator through matching it —
 * but it must NOT look like an alert: we have no idea whether the plate is
 * wrong, so neutral styling, no pulse.
 */
export type FixPlateState = 'needs_fix' | 'verified' | 'unlinked';

const STYLES: Record<FixPlateState, { className: string; title: string; label: string }> = {
  verified: {
    className: 'border-green-200 text-green-600 hover:bg-green-50 hover:border-green-300',
    title: 'Plate verified — click to re-fix if needed',
    label: 'Plate verified',
  },
  needs_fix: {
    className: 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 animate-pulse',
    title: 'Truck plate needs to be fixed on eModal',
    label: 'Fix truck plate',
  },
  unlinked: {
    className: 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400',
    title: 'Fix truck plate on eModal — this appointment was entered by hand, so we will match it on eModal first',
    label: 'Fix truck plate on eModal',
  },
};

export const FixPlateButton: React.FC<{
  state: FixPlateState;
  onClick: () => void;
  /** Stop the click from also triggering row-level handlers (open detail, drag). */
  stopPropagation?: boolean;
}> = ({ state, onClick, stopPropagation }) => {
  const style = STYLES[state] || STYLES.needs_fix;
  return (
    <button
      type="button"
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onClick();
      }}
      title={style.title}
      aria-label={style.label}
      className={
        'inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ' +
        style.className
      }
    >
      <TruckIcon className="w-3.5 h-3.5" />
    </button>
  );
};

export default FixPlateButton;
